import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { MapPin, Maximize, CircleDollarSign, ArrowLeft, Hash, User, IdCard, Megaphone, FileText, Ruler, Map } from "lucide-react";
import { useAuth } from "../context/AuthContext";


const formatMoney = (n) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

const STATUS_COLORS = {
  available: "bg-emerald-100 text-emerald-800 border-emerald-200",
  reserved: "bg-amber-100 text-amber-800 border-amber-200",
  sold: "bg-red-100 text-red-800 border-red-200",
};

const STATUS_LABELS = {
  available: "Disponible",
  reserved: "Reservado",
  sold: "Vendido",
};

export default function LotDetailDialog({
  lot,
  open,
  onOpenChange,
  onReserved,
  viewOnly = false,
}) {
  const isAdmin = localStorage.getItem("role") === "admin";
  const { user, ready, logout } = useAuth();
  const userRole = user?.role || "vendedor";

  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    id_client: "",
    notes: "",
    cliente: {
      nombre: "", apellido_paterno: "", apellido_materno: "", age: 0, fecha_nacimiento: "", telefono: "", email: ""
    },
    datos_cliente: {
      estatus_conyugal: "", nacionalidad: "", beneficiario: "", tipo_casa: "", dependientes: 0, occupacion: "", lugar_trabajo: "", antiguedad_trabajo: ""
    },
    direccion: {
      calle: "", numero: "", colonia: "", codigo_postal: "", municipio: "", estado: ""
    },
    contributions: {
      total_contribution: 0, total_contribution_text: "", reservation_payment: 0, initial_contribution: 0, monthly_contribution: 0, monthly_amount: 0, first_monthly_payment_day: "", notes: ""
    },
    promoter: {
      name: "", office_address: "", office_phone: "", office_email: ""
    }
  });

  useEffect(() => {
    if (!open) {
      setShowForm(false);
      setFormData({
        id_client: "", notes: "",
        cliente: { nombre: "", apellido_paterno: "", apellido_materno: "", age: 0, fecha_nacimiento: "", telefono: "" },
        datos_cliente: { estatus_conyugal: "", nacionalidad: "", beneficiario: "", tipo_casa: "", dependientes: 0, occupacion: "", lugar_trabajo: "", antiguedad_trabajo: "" },
        direccion: { calle: "", numero: "", colonia: "", codigo_postal: "", municipio: "", estado: "" },
        contributions: { total_contribution: 0, total_contribution_text: "", reservation_payment: 0, initial_contribution: 0, monthly_contribution: 0, monthly_amount: 0, first_monthly_payment_day: "", notes: "" },
        promoter: { name: "", office_address: "", office_phone: "", office_email: "" }
      });
    }
  }, [open]);

  if (!lot) return null;

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        lot_id: String(lot.id),
        id_lots: String(lot.id),
        id_client: formData.id_client ? Number(formData.id_client) : Math.floor(Math.random() * 1000000),
        cliente: {
          ...formData.cliente,
          email: formData.cliente.email || null
        },
        datos_cliente: formData.datos_cliente,
        direccion: formData.direccion,
        contributions: formData.contributions,
        promoter: formData.promoter,
        notes: formData.notes
      };

      const { data } = await api.post("/reservations", payload);
      
      toast.success("Reserva creada", {
        description: `Lote ${lot.id} reservado exitosamente.`,
      });
      
      onReserved && onReserved(data);
      onOpenChange(false);
    } catch (err) {
      let errorMsg = formatApiError(err);
      if (err.response?.status === 422 && err.response?.data?.detail) {
        const detail = err.response.data.detail[0];
        errorMsg = `Error en el campo '${detail.loc[detail.loc.length - 1]}': ${detail.msg}`;
      }
      toast.error("No se pudo crear la reserva", { description: errorMsg });
    } finally {
      setSubmitting(false);
    }
  };

  const isAvailable = lot.status === "available";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`p-0 overflow-hidden rounded-2xl bg-slate-50 transition-all duration-300 ${showForm ? 'max-w-5xl' : 'max-w-md'}`}>
        
        {/* ENCABEZADO */}
        <DialogHeader className="p-6 pb-4 border-b border-slate-200 bg-white flex flex-row items-center justify-between">
          <div>
            <DialogTitle className="font-display text-2xl font-bold flex items-center gap-2 text-slate-900">
              {showForm ? (
                <>
                  <Button variant="ghost" size="icon" onClick={() => setShowForm(false)} className="mr-1 h-8 w-8 text-slate-400 hover:text-slate-700 hover:bg-slate-100">
                    <ArrowLeft size={18} />
                  </Button>
                  Crear Reserva <span className="text-blue-600">Lote {lot.id}</span>
                </>
              ) : (
                `Detalles del Lote ${lot.id}`
              )}
            </DialogTitle>
          </div>
          
          {!showForm && (
            <span className={`px-3 py-1 rounded-full text-xs font-bold border uppercase tracking-wider shadow-sm ${STATUS_COLORS[lot.status]}`}>
              {STATUS_LABELS[lot.status]}
            </span>
          )}
        </DialogHeader>

        {/* CONTENIDO: VISTA DE DETALLES DEL LOTE */}
        {!showForm ? (
          <div className="p-6 space-y-5">
            
            <div className="bg-emerald-50 border border-emerald-100 p-5 rounded-xl flex items-center justify-between shadow-sm">
              <div>
                <span className="flex items-center gap-1.5 text-[10px] text-emerald-600 uppercase font-bold tracking-wider mb-1">
                  <CircleDollarSign size={14} /> Precio de Lista
                </span>
                <span className="font-display text-3xl font-bold text-emerald-700">
                  {formatMoney(lot.precio || lot.price)}
                </span>
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm grid grid-cols-2 gap-4 divide-x divide-slate-100">
              <div className="flex flex-col items-center justify-center text-center">
                <Map className="text-indigo-400 mb-1.5" size={20} />
                <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Manzana</span>
                <span className="font-display text-xl font-bold text-slate-900 mt-0.5">{lot.mz || "-"}</span>
              </div>
              <div className="flex flex-col items-center justify-center text-center">
                <MapPin className="text-blue-400 mb-1.5" size={20} />
                <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Lote</span>
                <span className="font-display text-xl font-bold text-slate-900 mt-0.5">{lot.lote || "-"}</span>
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm grid grid-cols-2 gap-4 divide-x divide-slate-100">
              <div className="flex flex-col items-center justify-center text-center">
                <Maximize className="text-amber-400 mb-1.5" size={20} />
                <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Área Total</span>
                <span className="font-display text-lg font-bold text-slate-800 mt-0.5">{lot.area || lot.size} m²</span>
              </div>
              <div className="flex flex-col items-center justify-center text-center">
                <Ruler className="text-purple-400 mb-1.5" size={20} />
                <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Perímetro</span>
                <span className="font-display text-lg font-bold text-slate-800 mt-0.5">
                  {lot.perimetro ? `${Number(lot.perimetro).toFixed(2)} m` : "-"}
                </span>
              </div>
            </div>

            {lot.descripcion && (
              <div className="bg-slate-100/50 rounded-xl p-4 text-sm text-slate-600 leading-relaxed border border-slate-200">
                <span className="block font-bold text-slate-700 mb-1">Descripción:</span>
                {lot.descripcion}
              </div>
            )}

            {!viewOnly && isAvailable && (
              <Button
                className="w-full h-12 mt-2 text-base font-medium shadow-lg shadow-emerald-600/20 bg-emerald-600 hover:bg-emerald-700 text-white transition-all rounded-xl"
                onClick={() => setShowForm(true)}
              >
                Comenzar Proceso de Reserva
              </Button>
            )}
          </div>
        ) : (
          /* CONTENIDO: FORMULARIO DE RESERVA */
          <form onSubmit={submit} className="overflow-y-auto max-h-[75vh]">
            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              
              <div className="space-y-6 ">
                <div className="p-5 bg-white border border-slate-200 rounded-xl shadow-sm hidden ">
                  <h4 className="font-bold text-sm flex items-center gap-2 text-slate-800 mb-4">
                    <Hash size={16} className="text-slate-400" /> Identificador
                  </h4>
                  <div className="space-y-1.5">
                    <Label htmlFor="id_client" className="text-xs font-semibold text-slate-500 uppercase tracking-wider">ID Cliente (Opcional)</Label>
                    <Input id="id_client" type="number" placeholder="Ej. 1" className="h-10 font-mono bg-slate-50 max-w-[200px]" value={formData.id_client} onChange={(e) => setFormData({ ...formData, id_client: e.target.value })} />
                  </div>
                </div>

                <div className="p-5 bg-white border border-slate-200 rounded-xl shadow-sm">
                  <h4 className="font-bold text-sm flex items-center gap-2 text-slate-800 border-b border-slate-100 pb-2 mb-4">
                    <User size={16} className="text-blue-500" /> Información Principal
                  </h4>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="space-y-1.5"><Label className="text-slate-600">Nombre *</Label><Input required className="h-10" value={formData.cliente.nombre} onChange={(e) => handleNestedChange("cliente", "nombre", e.target.value)} /></div>
                      <div className="space-y-1.5"><Label className="text-slate-600">Ap. Paterno</Label><Input className="h-10" value={formData.cliente.apellido_paterno} onChange={(e) => handleNestedChange("cliente", "apellido_paterno", e.target.value)} /></div>
                      <div className="space-y-1.5"><Label className="text-slate-600">Ap. Materno</Label><Input className="h-10" value={formData.cliente.apellido_materno} onChange={(e) => handleNestedChange("cliente", "apellido_materno", e.target.value)} /></div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="space-y-1.5"><Label className="text-slate-600">Edad</Label><Input type="number" className="h-10" value={formData.cliente.age || ""} onChange={(e) => handleNestedChange("cliente", "age", Number(e.target.value))} /></div>
                      <div className="space-y-1.5"><Label className="text-slate-600">F. Nacimiento</Label><Input type="date" className="h-10" value={formData.cliente.fecha_nacimiento} onChange={(e) => handleNestedChange("cliente", "fecha_nacimiento", e.target.value)} /></div>
                      <div className="space-y-1.5"><Label className="text-slate-600">Teléfono *</Label><Input required className="h-10" value={formData.cliente.telefono} onChange={(e) => handleNestedChange("cliente", "telefono", e.target.value)} /></div>
                    </div>
                    <div className="space-y-1.5"><Label className="text-slate-600">Correo Electrónico</Label><Input type="email" className="h-10" value={formData.cliente.email} onChange={(e) => handleNestedChange("cliente", "email", e.target.value)} /></div>
                  </div>
                </div>

                <div className="p-5 bg-white border border-slate-200 rounded-xl shadow-sm">
                  <h4 className="font-bold text-sm flex items-center gap-2 text-slate-800 border-b border-slate-100 pb-2 mb-4">
                    <MapPin size={16} className="text-emerald-500" /> Dirección de Residencia
                  </h4>
                  <div className="space-y-3">
                    <div className="grid grid-cols-4 gap-3">
                      <div className="col-span-3 space-y-1.5"><Label className="text-slate-600">Calle</Label><Input className="h-10" value={formData.direccion.calle} onChange={(e) => handleNestedChange("direccion", "calle", e.target.value)} /></div>
                      <div className="space-y-1.5"><Label className="text-slate-600">Número</Label><Input className="h-10" value={formData.direccion.numero} onChange={(e) => handleNestedChange("direccion", "numero", e.target.value)} /></div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1.5"><Label className="text-slate-600">Colonia</Label><Input className="h-10" value={formData.direccion.colonia} onChange={(e) => handleNestedChange("direccion", "colonia", e.target.value)} /></div>
                      <div className="space-y-1.5"><Label className="text-slate-600">CP</Label><Input className="h-10" value={formData.direccion.codigo_postal} onChange={(e) => handleNestedChange("direccion", "codigo_postal", e.target.value)} /></div>
                      <div className="space-y-1.5"><Label className="text-slate-600">Municipio</Label><Input className="h-10" value={formData.direccion.municipio} onChange={(e) => handleNestedChange("direccion", "municipio", e.target.value)} /></div>
                      <div className="space-y-1.5"><Label className="text-slate-600">Estado</Label><Input className="h-10" value={formData.direccion.estado} onChange={(e) => handleNestedChange("direccion", "estado", e.target.value)} /></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                {/* ESQUEMA DE PAGOS (SOLO ADMIN) */}
                {userRole === 'admin' && (
                  <div className="p-5 bg-white border border-slate-200 rounded-xl shadow-sm">
                    <h4 className="font-bold text-sm flex items-center gap-2 text-slate-800 border-b border-slate-100 pb-2 mb-4">
                      <CircleDollarSign size={16} className="text-amber-500" /> Esquema de Pagos
                    </h4>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5"><Label className="text-slate-600">Valor Total Lote</Label><Input type="number" className="h-10 font-mono" value={formData.contributions.total_contribution || ""} onChange={(e) => handleNestedChange("contributions", "total_contribution", Number(e.target.value))} /></div>
                        <div className="space-y-1.5"><Label className="text-slate-600">Pago de Apartado</Label><Input type="number" className="h-10 font-mono" value={formData.contributions.reservation_payment || ""} onChange={(e) => handleNestedChange("contributions", "reservation_payment", Number(e.target.value))} /></div>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg border border-slate-100 grid grid-cols-2 gap-4">
                        <div className="space-y-1.5"><Label className="text-slate-600">Plazo (Meses)</Label><Input type="number" className="h-10" value={formData.contributions.monthly_contribution || ""} onChange={(e) => handleNestedChange("contributions", "monthly_contribution", Number(e.target.value))} /></div>
                        <div className="space-y-1.5"><Label className="text-slate-600">Monto Mensual</Label><Input type="number" className="h-10 font-mono" value={formData.contributions.monthly_amount || ""} onChange={(e) => handleNestedChange("contributions", "monthly_amount", Number(e.target.value))} /></div>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-slate-600">Valor en Texto</Label>
                        <Input className="h-10" placeholder="Ej. Cien mil pesos..." value={formData.contributions.total_contribution_text} onChange={(e) => handleNestedChange("contributions", "total_contribution_text", e.target.value)} />
                      </div>
                    </div>
                  </div>
                )}

                <div className="p-5 bg-white border border-slate-200 rounded-xl shadow-sm">
                  <h4 className="font-bold text-sm flex items-center gap-2 text-slate-800 border-b border-slate-100 pb-2 mb-4">
                    <Megaphone size={16} className="text-indigo-500" /> Datos del Promotor
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label className="text-slate-600">Nombre</Label><Input className="h-10" value={formData.promoter.name} onChange={(e) => handleNestedChange("promoter", "name", e.target.value)} /></div>
                    <div className="space-y-1.5"><Label className="text-slate-600">Teléfono</Label><Input className="h-10" value={formData.promoter.office_phone} onChange={(e) => handleNestedChange("promoter", "office_phone", e.target.value)} /></div>
                  </div>
                </div>

                <div className="p-5 bg-amber-50/50 border border-amber-100 rounded-xl shadow-sm">
                  <h4 className="font-bold text-sm flex items-center gap-2 text-amber-800 border-b border-amber-200/60 pb-2 mb-4">
                    <FileText size={16} className="text-amber-500" /> Observaciones
                  </h4>
                  <Textarea id="create_notes" rows={4} className="bg-white resize-none" placeholder="Acuerdos especiales..." value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
                </div>
              </div>
            </div>

            <DialogFooter className="p-6 bg-white border-t border-slate-200 gap-3">
              <Button type="button" variant="outline" className="h-11 px-6 border-slate-300" onClick={() => setShowForm(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="h-11 px-8 bg-slate-900 hover:bg-slate-800 text-white shadow-lg" disabled={submitting}>
                {submitting ? "Guardando..." : "Confirmar Reserva"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}